import React from "react";
import QueryBuilder from "./QueryBuilder";
import { Button } from "@components/ui/button";
import { CirclePlus, Trash2 } from "lucide-react";
import { DefinedVariable, RequestBlock } from "./allData";
import Confirmation from "@components/common/ConfirmDialog";
import { useMutation, useQuery } from "react-query";
import { commonDeleteApi, fetchLogicPageInfo } from "./createPluginApi";
import { toast } from "@components/ui/use-toast";
import PageLoader from "@components/loaders/PageLoader";
import { useMeshPluginContext } from "@services/providers/MeshPlugin";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
} from "@components/ui/dropdown-menu";

const THREAT_LOOKUP_OPTIONS = [
  "Choose a type",
  "common",
  "domain",
  "email",
  "file",
  "hash",
  "ip",
  "none",
  "url",
];

type Props = {
  setSelectedApiData?: (data: any) => void;
  setDefinedVariables?: (variables: DefinedVariable[]) => void;
  definedVariables?: DefinedVariable[];
  logicType?: any;
  setLogicType?: (type: any) => void;
  selectedLogicBlockId: number | null;
  pluginId?: string | null;
  setLogMappedFields?: React.Dispatch<React.SetStateAction<any[]>>;
  setMappingSaveData?: React.Dispatch<React.SetStateAction<any[]>>;
  mappingSaveData?: any;
  onThreatLookupChange?: (requestId: number, type: string) => void;
  threatLookupMap?: Record<number, string>;
};

const QueryChainManager: React.FC<Props> = ({
  setSelectedApiData,
  setDefinedVariables,
  definedVariables,
  logicType,
  setLogicType,
  selectedLogicBlockId,
  pluginId,
  setLogMappedFields,
  setMappingSaveData,
  mappingSaveData,
  onThreatLookupChange,
  threatLookupMap = {},
}) => {
  const { selectedLogicName, hasLogicPage } = useMeshPluginContext();

  // Check if we're in edit mode
  const isEditMode = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("edit") === "1";
  }, []);

  // Fetch logic page data from backend to check if requests are saved
  const { data: fetchedLogicPageData } = useQuery({
    queryKey: ["fetchLogicPageData", pluginId],
    queryFn: () => fetchLogicPageInfo(pluginId!),
    enabled: !!pluginId && isEditMode && hasLogicPage,
    refetchOnMount: "always",
    staleTime: 0,
  });

  // Get the current logic block from the logicType
  const selectedLogicBlock = React.useMemo(() => {
    const logic = logicType?.find(
      (item: any) => item.name === selectedLogicName,
    );
    return (
      logic?.data?.find((block: any) => block.id === selectedLogicBlockId) ||
      null
    );
  }, [logicType, selectedLogicName, selectedLogicBlockId]);

  const [localBlocks, setLocalBlocks] = React.useState<RequestBlock[]>([
    { id: 0, requestId: 1, response: "" },
  ]);
  const [finalRequestData, setFinalRequestData] = React.useState<any>(null);
  const [isResponse, setIsResponse] = React.useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] =
    React.useState<boolean>(false);
  const [deleteIds, setDeleteIds] = React.useState<{
    reqId: any;
    bundleId: any;
  }>({
    reqId: null,
    bundleId: null,
  });
  const [loading, setLoading] = React.useState<boolean>(false);

  // mutation for delete request block
  const deleteRequestBlock = useMutation({
    mutationFn: (payload: any) =>
      commonDeleteApi(
        payload.plugin_id,
        payload.delete_type,
        payload.bundle_uid,
        payload.request_uid,
        payload.variable_uid,
      ),

    onMutate: () => {
      setLoading(true);
    },

    onSettled: () => {
      setLoading(false);
    },

    onSuccess: () => {
      // remove request from localBlocks
      const { reqId: BLOCK_ID, bundleId: BUNDLE_ID } = deleteIds;
      const isOnlyBlock = localBlocks.length === 1;

      setLocalBlocks((prev) => {
        const filtered = prev.filter((b) => b.requestId !== BLOCK_ID);
        // If no blocks remain, add a new initial block
        if (filtered.length === 0) {
          return [{ id: 0, requestId: 1, response: "" }];
        }
        return filtered;
      });

      // remove request from finalRequestData
      setFinalRequestData((prev: any) => {
        if (!prev) return prev;

        // If this was the only block, reset finalRequestData
        if (isOnlyBlock) {
          return {
            request: [],
            variables: prev.variables || [],
            responses: {},
          };
        }

        return {
          ...prev,
          request: (prev.request || []).filter(
            (r: any) => r.requestId !== BLOCK_ID,
          ),
          responses: Object.fromEntries(
            Object.entries(prev.responses || {}).filter(
              ([key]) => Number(key) !== BLOCK_ID,
            ),
          ),
        };
      });

      // Remove variables associated with this request
      const updatedVariables = definedVariables?.filter(
        (variable) =>
          !(
            variable.sourceBundleId === BUNDLE_ID &&
            variable.sourceRequestId === BLOCK_ID
          ),
      );

      setDefinedVariables?.(updatedVariables || []);

      // Show appropriate toast message
      if (isOnlyBlock) {
        toast({
          title: "Request Deleted",
          description:
            "Last request deleted. A new request block has been created.",
          variant: "success",
          duration: 3000,
        });
      } else {
        toast({
          title: "Request Deleted",
          description: "Request has been deleted successfully.",
          variant: "success",
          duration: 3000,
        });
      }

      setIsDeleteDialogOpen(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: `Failed to delete the request. Please try again.`,
        variant: "destructive",
        duration: 3000,
      });
      setIsDeleteDialogOpen(false);
    },
  });

  // Sync localBlocks and finalRequestData when selectedLogicBlock changes
  React.useEffect(() => {
    if (!selectedLogicBlock) return;

    const loadedBlocks = selectedLogicBlock.blocks || [
      { id: 0, requestId: 1, response: "" },
    ];

    // Fix: Ensure requestId always exists
    const fixedBlocks = loadedBlocks.map((b: any, index: number) => ({
      ...b,
      requestId: b.requestId ?? index + 1,
    }));

    setLocalBlocks(fixedBlocks);
    setFinalRequestData(selectedLogicBlock.finalRequestData || null);
  }, [selectedLogicBlock?.id]);

  // Compute isResponse from localBlocks (last block has response)
  React.useEffect(() => {
    const lastBlock = localBlocks[localBlocks.length - 1];
    const hasResponse =
      lastBlock && lastBlock.response && lastBlock.response !== "";
    setIsResponse(hasResponse);
  }, [localBlocks]);

  // Update logicType when local state changes
  React.useEffect(() => {
    if (!selectedLogicName || !setLogicType || selectedLogicBlockId === null)
      return;

    setLogicType((prevLogicType: any[]) => {
      return prevLogicType.map((logic) => {
        if (logic.name !== selectedLogicName) return logic;

        const updatedData = logic.data.map((block: any) =>
          block.id === selectedLogicBlockId
            ? {
                ...block,
                blocks: localBlocks,
                finalRequestData,
                isResponse,
              }
            : block,
        );

        return { ...logic, data: updatedData };
      });
    });
  }, [localBlocks, finalRequestData, isResponse]);

  // Function to update a specific block's response
  const setBlockResponse = (blockId: number, response: any) => {
    setLocalBlocks((prevBlocks) =>
      prevBlocks.map((block) =>
        block.id === blockId ? { ...block, response } : block,
      ),
    );
  };

  // Add a new block to the local state
  // const addBlock = () => {
  //   const nextId =
  //     localBlocks.length > 0
  //       ? Math.max(...localBlocks.map((b) => b.id)) + 1
  //       : 1;
  //   const newBlock = { id: nextId, response: "" };
  //   setLocalBlocks((prev) => [...prev, newBlock]);
  // };

  const addBlock = () => {
    setLocalBlocks((prev: any) => {
      const newRequestId =
        prev.length > 0
          ? Math.max(...prev.map((b: any) => b.requestId)) + 1
          : 1;
      const id =
        prev.length > 0 ? Math.max(...prev.map((b: any) => b.id)) + 1 : 1;
      return [
        ...prev,
        {
          id,
          requestId: newRequestId,
          response: "",
        },
      ];
    });
  };

  const updateRequest: any = (requestId: number, updatedFields: any) => {
    setFinalRequestData((prev: any) => {
      if (!prev) return prev;

      const updatedRequests = (prev.request || []).map((req: any) =>
        req.requestId === requestId ? { ...req, ...updatedFields } : req,
      );

      return { ...prev, request: updatedRequests };
    });
  };

  // Function to delete a request block
  const handleDeleteRequest = (reqId: any, bundleId: any) => {
    setIsDeleteDialogOpen(true);
    setDeleteIds({ reqId, bundleId });
  };

  // Confirm deletion
  const deleteRequest = () => {
    const { reqId: BLOCK_ID, bundleId: BUNDLE_ID } = deleteIds;

    // Check if the request exists in the backend (saved)
    const isSavedInServer = fetchedLogicPageData?.data?.LogicBundles?.some(
      (bundle: any) => {
        if (bundle.bundleId !== BUNDLE_ID) return false;
        return (bundle.requests || []).some(
          (req: any) => req.requestId === BLOCK_ID,
        );
      },
    );

    // If the request is NOT saved in server (unsaved/local only), delete from state only
    if (!isSavedInServer) {
      // Check if this is the only block remaining
      const isOnlyBlock = localBlocks.length === 1;

      // delete the request from state
      setLocalBlocks((prev) => {
        const filtered = prev.filter((b) => {
          const effectiveRequestId = b.requestId || b.id + 1;
          return effectiveRequestId !== BLOCK_ID;
        });
        // If no blocks remain, add a new initial block
        if (filtered.length === 0) {
          return [{ id: 0, requestId: 1, response: "" }];
        }
        return filtered;
      });

      // remove request from finalRequestData
      setFinalRequestData((prev: any) => {
        if (!prev) return prev;

        const filteredRequests = (prev.request || []).filter(
          (r: any) => r.requestId !== BLOCK_ID,
        );

        const filteredResponses = Object.fromEntries(
          Object.entries(prev.responses || {}).filter(
            ([key]) => Number(key) !== BLOCK_ID,
          ),
        );

        // If this was the only block, reset finalRequestData
        if (isOnlyBlock) {
          return {
            request: [],
            variables: prev.variables || [],
            responses: {},
          };
        }

        return {
          ...prev,
          request: filteredRequests,
          responses: filteredResponses,
        };
      });

      // Remove variables associated with this request
      const updatedVariables = definedVariables?.filter(
        (variable) =>
          !(
            variable.sourceBundleId === BUNDLE_ID &&
            variable.sourceRequestId === BLOCK_ID
          ),
      );

      setDefinedVariables?.(updatedVariables || []);

      // Show appropriate toast message
      if (isOnlyBlock) {
        toast({
          title: "Request Deleted",
          description:
            "Last request deleted. A new request block has been created.",
          variant: "success",
          duration: 3000,
        });
      } else {
        toast({
          title: "Request Deleted",
          description: "Request has been deleted successfully.",
          variant: "success",
          duration: 3000,
        });
      }

      setIsDeleteDialogOpen(false);
      return;
    }

    // If the request IS saved in server, call the delete API
    deleteRequestBlock.mutate({
      plugin_id: pluginId,
      delete_type: "request",
      bundle_uid: BUNDLE_ID,
      request_uid: BLOCK_ID,
      variable_uid: "",
    });
  };

  return (
    <div>
      {isDeleteDialogOpen && (
        <Confirmation
          text={
            "Are you sure you want to delete this request? This action cannot be undone."
          }
          confirmAction={deleteRequest}
          rejectAction={() => setIsDeleteDialogOpen(false)}
          confirmText="Yes, delete it!"
          rejectText="Cancel"
          show={isDeleteDialogOpen}
          closeDialog={() => setIsDeleteDialogOpen(false)}
        />
      )}

      <PageLoader show={loading} />

      {localBlocks.map((block, index) => (
        <div
          key={block.id}
          className="p-4 border-2 rounded-md bg-primary-background shadow-sm mb-4"
        >
          {selectedLogicName === "Threat Lookup" && (
            <div className="mb-2">
              <label className="font-bold text-sm text-primaryBlue block ml-1 mb-1">
                Threat Lookup Type
              </label>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-[220px] justify-between"
                  >
                    {block.requestId !== undefined &&
                    threatLookupMap[block.requestId as number]
                      ? threatLookupMap[block.requestId as number]
                      : "Choose a type"}
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent className="w-[220px]">
                  {THREAT_LOOKUP_OPTIONS.map((type) => {
                    const usedByOtherBlock = Object.entries(
                      threatLookupMap,
                    ).some(
                      ([reqId, value]) =>
                        Number(reqId) !== block.requestId && value === type,
                    );

                    return (
                      <DropdownMenuCheckboxItem
                        key={type}
                        checked={
                          block.requestId !== undefined &&
                          threatLookupMap[block.requestId] === type
                        }
                        disabled={usedByOtherBlock}
                        onCheckedChange={(checked) => {
                          if (
                            checked &&
                            onThreatLookupChange &&
                            block.requestId !== undefined
                          ) {
                            onThreatLookupChange(block.requestId, type);
                          }
                        }}
                      >
                        {type}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          <div className="flex justify-between items-center mb-4">
            <p className="text-muted-foreground text-md font-semibold ">
              Request #{block.requestId || block.id + 1}
            </p>
            <div
              className=" cursor-pointer text-muted-foreground hover:text-primary p-1"
              onClick={() =>
                handleDeleteRequest(
                  block.requestId || block.id + 1,
                  selectedLogicBlockId,
                )
              }
            >
              <Trash2 size={15} />
            </div>
          </div>
          <QueryBuilder
            previousResponse={
              index > 0 ? localBlocks[index - 1].response : null
            }
            setSelectedApiData={setSelectedApiData}
            requestId={block.requestId || block.id + 1}
            setDefinedVariables={setDefinedVariables}
            definedVariables={definedVariables}
            finalRequestData={finalRequestData}
            setFinalRequestData={setFinalRequestData}
            setBlockResponse={setBlockResponse}
            blockId={block.id}
            selectedLogicBlockId={selectedLogicBlockId}
            pluginId={pluginId}
            setLogMappedFields={setLogMappedFields}
            setMappingSaveData={setMappingSaveData}
            mappingSaveData={mappingSaveData}
            updateRequest={updateRequest}
          />
        </div>
      ))}

      {/* {isResponse && ( */}
      <div className="flex justify-between items-center mt-4">
        <Button
          onClick={addBlock}
          className="bg-transparent hover:text-muted-foreground border-none translate-color text-primaryBlue"
        >
          <CirclePlus size={16} className="mr-1" /> Add API Request
        </Button>

        {/* {selectedLogicName === "Save Logs" && (
            <Button
              onClick={() => setOpenMapfieldsDialog({ open: true, requestId: null })}
              className="bg-transparent hover:text-background border border-primary hover:bg-primary translate-color text-foreground"
            >
              Map fields
            </Button>
          )} */}
      </div>
      {/* )} */}
    </div>
  );
};

export default QueryChainManager;
